import { Box, Button, Grid, IconButton, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import AppDialog from "../../components/app-dialog/AppDialog";
import Iconify from "../../components/iconify/Iconify"; // Assuming you have this
import { addItem, setLastAddedItemId } from "../../reduxStore/slices/cartSlice";
import ItemAttributeCard from "./ItemAttributeCard";
import ItemCard from "./ItemCard";
import ItemVariantAddonSelection from "./ItemVariantAddonSelection";

export default function ItemVariantSelectionDialog({ open, onClose, item }) {
  const dispatch = useDispatch();
  const [selectedAddons, setSelectedAddons] = useState({});
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [selectedAddonsWithDetails, setSelectedAddonsWithDetails] = useState(
    {}
  );

  const itemVariants = item?.variants || [];
  const itemAddOns = item.addons || [];

  console.log("xxxxxxxxxxxxxxxxxxx", item);

  let hasVariants = itemVariants.length > 1;

  const hasAttributes = itemVariants[0].attributes || [];

  useEffect(() => {
    if (!item?.addons?.length) return;

    const updatedDetails = {};

    Object.entries(selectedAddons).forEach(([id, qty]) => {
      const addonDetails = item.addons.find(
        (addon) => addon.product_addon_uid === Number(id)
      );

      if (addonDetails) {
        updatedDetails[id] = {
          ...addonDetails,
          qty: qty,
        };
      }
    });

    setSelectedAddonsWithDetails(updatedDetails);
  }, [selectedAddons, item]);

  let hasAddons = itemVariants.some(
    (variant) => variant.addons && variant.addons.length > 0
  );

  let subtitle = "";
  if (hasVariants && hasAddons) {
    subtitle = "Select Variation & Add-ons";
  } else if (hasVariants) {
    subtitle = "Select Variation";
  } else if (hasAddons) {
    subtitle = "Select Add-ons";
  } else {
    subtitle = "Item Details";
  }

  const buildAttributeGroups = (variants = []) => {
    const map = {};
    variants.forEach((variant) => {
      variant.attributes.forEach((attr) => {
        if (!map[attr.attribute_name]) {
          map[attr.attribute_name] = new Set();
        }
        map[attr.attribute_name].add(attr.attribute_value);
      });
    });

    return Object.entries(map).map(([name, values]) => ({
      name,
      values: Array.from(values),
    }));
  };

  const attributeGroups = useMemo(
    () => buildAttributeGroups(itemVariants),
    [itemVariants]
  );

  const isAttributeValueAvailable = (
    variants,
    selectedAttributes,
    currentGroupName,
    valueToCheck
  ) => {
    return variants.some((variant) => {
      // Must contain the candidate value
      const hasCandidate = variant.attributes.some(
        (a) =>
          a.attribute_name === currentGroupName &&
          a.attribute_value === valueToCheck
      );

      if (!hasCandidate) return false;

      // Must match other selected attributes
      return Object.entries(selectedAttributes).every(
        ([attrName, attrValue]) => {
          if (attrName === currentGroupName) return true;

          return variant.attributes.some(
            (a) =>
              a.attribute_name === attrName && a.attribute_value === attrValue
          );
        }
      );
    });
  };

  const handleAttributeSelect = (groupName, value) => {
    setSelectedAttributes((prev) => {
      // Toggle OFF if already selected
      if (prev[groupName] === value) {
        const next = { ...prev };
        delete next[groupName];
        return next;
      }

      // Select new value
      return {
        ...prev,
        [groupName]: value,
      };
    });
  };

  const findExactMatchingVariant = (variants, selectedAttributes) => {
    const selectedEntries = Object.entries(selectedAttributes);
    if (selectedEntries.length === 0) return null;

    return (
      variants.find((variant) => {
        // same number of attributes
        if (variant.attributes.length !== selectedEntries.length) {
          return false;
        }

        // every selected attribute must exist
        return selectedEntries.every(([name, value]) =>
          variant.attributes.some(
            (a) => a.attribute_name === name && a.attribute_value === value
          )
        );
      }) || null
    );
  };

  const selectedVariant = useMemo(() => {
    if (hasAttributes.length > 0) {
      return findExactMatchingVariant(itemVariants, selectedAttributes);
    } else {
      return itemVariants[0];
    }
  }, [itemVariants, selectedAttributes]);

  const price = selectedVariant?.current_price?.final_price ?? 0;
  const finalPrice = price * quantity;

  const onAddClick = () => {
    if (!selectedVariant) return;
    const addonsArray = Object.values(selectedAddonsWithDetails);
    dispatch(
      addItem({ ...selectedVariant, selectedAddons: addonsArray, quantity: quantity })
    );
    dispatch(
      setLastAddedItemId({ id: selectedVariant.variant_uid, ts: Date.now() })
    );
    // Close dialog
    onClose();
  };

  const handleAddItem = (itemVariant, item) => {
    setQuantity((prev) => prev + 1);
  };

  // Mock Data based on your image
  const variants = [
    { id: "half", label: "Half", price: 180 },
    { id: "full", label: "Full", price: 320 },
  ];

  const availableAddons = [
    { id: "butter", label: "Extra Butter", price: 20 },
    { id: "cheese", label: "Cheese", price: 30 },
    { id: "gravy", label: "Extra Gravy", price: 25 },
  ];

  // // Calculate Total
  // const variantPrice = variants.find((v) => v.id === variant)?.price || 0;
  // const addonsPrice = Object.entries(addons).reduce((total, [id, qty]) => {
  //   const addon = availableAddons.find((a) => a.id === id);
  //   return total + addon.price * qty;
  // }, 0);

  // const finalPrice = (variantPrice + addonsPrice) * quantity;

  const dialogFooter = (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      {/* Master Quantity for the whole Dish */}
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton
          onClick={() => setQuantity((q) => Math.max(1, q - 1))}
          sx={{ bgcolor: "grey.200" }}
        >
          <Iconify icon="mdi:minus" width={18} />
        </IconButton>
        <Typography variant="h6">{quantity}</Typography>
        <IconButton
          onClick={() => setQuantity((q) => q + 1)}
          sx={{
            bgcolor: "primary.main",
            color: "white",
            "&:hover": { bgcolor: "primary.dark" },
          }}
        >
          <Iconify icon="mdi:plus" width={18} />
        </IconButton>
      </Box>

      <Box display="flex" gap={1}>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={!selectedVariant}
          variant="contained"
          onClick={onAddClick}
          size="large"
          sx={{ px: 3 }}
        >
          Add • ₹{finalPrice}
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <AppDialog
        maxWidth="sm"
        isOpen={open}
        id={"add_item_variant_dialog"}
        title={
          <Box sx={{ pb: 1 }}>
            {item?.product_name}
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
        }
        footer={dialogFooter}
      >
        <Box
          style={{
            margin: "20px 5px",
          }}
        >
          {attributeGroups.length > 0 ? (
            <Box>
              {attributeGroups.map((group) => (
                <Box key={group.name} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                    {group.name} <span style={{ color: "red" }}>*</span>
                  </Typography>

                  <Grid container spacing={2}>
                    {group.values.map((value) => {
                      const isSelected =
                        selectedAttributes[group.name] === value;

                      const isAvailable = isAttributeValueAvailable(
                        itemVariants,
                        selectedAttributes,
                        group.name,
                        value
                      );

                      const price =
                        group.name === attributeGroups[0].name && isAvailable
                          ? itemVariants.find((v) =>
                              v.attributes.some(
                                (a) =>
                                  a.attribute_name === group.name &&
                                  a.attribute_value === value
                              )
                            )?.current_price?.final_price
                          : undefined;

                      return (
                        <Grid item xs={6} key={value}>
                          <ItemAttributeCard
                            label={value}
                            price={price}
                            selected={isSelected}
                            disabled={!isAvailable}
                            onClick={() => {
                              if (!isAvailable && !isSelected) return;
                              handleAttributeSelect(group.name, value);
                            }}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </Box>
          ) : (
            <Box>
              <ItemCard
                key={itemVariants.product_uid}
                item={item}
                type="Variant"
                onAddItem={handleAddItem}
              />
            </Box>
          )}
          {itemAddOns.length > 0 && (
            <ItemVariantAddonSelection
              selectedAddons={selectedAddons}
              setSelectedAddons={setSelectedAddons}
              itemAddons={itemAddOns}
            />
          )}
        </Box>
      </AppDialog>
    </>
  );
}
